import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import {
  TOW_PRO,
  setCoordinates,
} from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import { needsWebMercatorConversion } from '../../../../../utils/needsWebMercatorConversion';
import type { GeocodedAddress } from '../../../../AirAmbulanceForm/AirAmbulanceForm/components/SuggestedAddress';
import useDebouncedAddressUpdate from '../../../../AirAmbulanceForm/AirAmbulanceForm/hooks/useDebouncedAddressUpdate';
import useFormReverseGeocode from '../../../../AirAmbulanceForm/AirAmbulanceForm/hooks/useFormReverseGeocode';
import { formatAddressFromObject } from '../../../../AirAmbulanceForm/AirAmbulanceForm/utils';
import usePermissionedGeocoder from '../../../AviveAED/usePermissionedGeocoder';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface GeoSelectionState {
  geoSelection: {
    [TOW_PRO]: { coordinates: [number, number] | null };
  };
}

interface UseTowProLocationFieldOptions {
  /** Applies a resolved address + coordinates to the form. */
  onLocationResolved: (location: {
    address?: string;
    latitude?: number;
    longitude?: number;
  }) => void;
}

/**
 * Location state for the tow request form: forward geocoding with
 * suggestions for typed addresses, and reverse geocoding for coordinates
 * picked on the map through the towPro geoSelection key.
 */
export function useTowProLocationField({
  onLocationResolved,
}: UseTowProLocationFieldOptions) {
  const dispatch = useDispatch();
  const mapClickCoordinates = useSelector(
    (state: GeoSelectionState) => state.geoSelection[TOW_PRO].coordinates,
  );

  const [addressToGeocode, setAddressToGeocode] = useState('');
  const [coordsToReverseGeocode, setCoordsToReverseGeocode] =
    useState<Coordinates | null>(null);

  const service = useWhichReverseGeocodeService();
  const geocodedResults = usePermissionedGeocoder(
    addressToGeocode,
  ) as GeocodedAddress[];
  const { isReverseGeocoding, foundAddress } = useFormReverseGeocode({
    coordinates: coordsToReverseGeocode,
    service,
  });
  const debouncedSetAddress = useDebouncedAddressUpdate(setAddressToGeocode);

  // Map click: geoSelection delivers [longitude, latitude]; consume it, then
  // clear the slice key so the same click is not reapplied.
  useEffect(() => {
    if (!mapClickCoordinates) {
      return;
    }
    const [longitude, latitude] = mapClickCoordinates;
    onLocationResolved({ latitude, longitude });
    setAddressToGeocode('');
    setCoordsToReverseGeocode({ latitude, longitude });
    dispatch(setCoordinates({ key: TOW_PRO, coordinates: null }));
  }, [mapClickCoordinates, dispatch, onLocationResolved]);

  // Reverse geocoding fills the address for coordinates picked on the map.
  useEffect(() => {
    if (!foundAddress) {
      return;
    }
    const formattedAddress = foundAddress.addressObject
      ? formatAddressFromObject(foundAddress.addressObject)
      : foundAddress.address;
    if (formattedAddress) {
      onLocationResolved({ address: formattedAddress });
    }
  }, [foundAddress, onLocationResolved]);

  const handleAddressChange = useCallback(
    (value: string): void => {
      // Typing invalidates the previously resolved coordinates until a
      // suggestion is picked.
      onLocationResolved({
        address: value,
        latitude: undefined,
        longitude: undefined,
      });
      if (value.trim().length > 3) {
        debouncedSetAddress(value);
      } else {
        setAddressToGeocode('');
      }
    },
    [debouncedSetAddress, onLocationResolved],
  );

  const handleSelectSuggestion = useCallback(
    (suggestion: GeocodedAddress): void => {
      const { x, y } = suggestion.location ?? {};
      if (typeof x !== 'number' || typeof y !== 'number') {
        return;
      }

      let latitude = y;
      let longitude = x;
      if (needsWebMercatorConversion(x, y)) {
        [longitude, latitude] = webMercatorUtils.xyToLngLat(x, y);
      }

      onLocationResolved({
        address: suggestion.address ?? '',
        latitude,
        longitude,
      });
      setAddressToGeocode('');
    },
    [onLocationResolved],
  );

  return {
    geocodedResults,
    isReverseGeocoding,
    handleAddressChange,
    handleSelectSuggestion,
    /** Resolve an address for coordinates that arrived without one. */
    triggerReverseGeocode: setCoordsToReverseGeocode,
  };
}
